import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { garageApi, vehiclesApi } from "../api/endpoints"
import { ApiError } from "../api/client"
import { useTelegram } from "../hooks/useTelegram"
import { openSupportBot } from "../utils/supportBot"
import { useI18n } from "../hooks/useI18n"
import { garageUi } from "../i18n/garageUi"
import type { Garage, JournalEntry, VehicleCard, VehicleStatus } from "../types/api"
import { GarageActivationSheet, GarageLifecycleSheet, GarageSuccessNotification } from "./GarageLifecycleSheets"
import { GarageLogbookSheet, GarageStatusSheet } from "./GarageFeatureSheets"
import "./GaragePage.figma.css"
import { useGarageActivation } from "../hooks/useGarageActivation"
import { GarageBackdrop } from "./GarageBackdrop"
import { AutoclanPage } from "./AutoclanPage"
import { PartnerPage } from "./PartnerPage"
import { useVehicleStore } from "../store/vehicleStore"
import { useAuthStore } from "../store/authStore"
import { normalizePromoInput } from "../utils/promo"
import { completeStarsInvoice } from "../utils/starsPayment"

const GARAGE_RENEWAL_PRICE_STARS = 990

const DEV_LOGBOOK_FIGMA_FIXTURE: JournalEntry[] = [
  {
    id: -101,
    entry_type: "signal",
    created_at: "2026-08-23T04:58:00+03:00",
    signal_type: { id: -101, slug: "bad_parking", title: "Неправильно припарковался", icon: "", color: "", category: "road" },
    allow_reply: false,
    reply_token: null,
  },
  {
    id: -102,
    entry_type: "signal",
    created_at: "2026-08-23T03:41:00+03:00",
    signal_type: { id: -102, slug: "car_being_towed", title: "Машину эвакуируют", icon: "", color: "", category: "urgent" },
    allow_reply: true,
    reply_token: "dev-figma-reply",
  },
  {
    id: -103,
    entry_type: "signal",
    created_at: "2026-08-22T21:06:00+03:00",
    signal_type: { id: -103, slug: "thanks", title: "Спасибо", icon: "", color: "", category: "attention" },
    allow_reply: false,
    reply_token: null,
  },
  {
    id: -104,
    entry_type: "signal",
    created_at: "2026-08-22T18:15:00+03:00",
    signal_type: { id: -104, slug: "cut_off", title: "Подрезал", icon: "", color: "", category: "road" },
    allow_reply: true,
    reply_token: "dev-figma-reply",
  },
  {
    id: -105,
    entry_type: "signal",
    created_at: "2026-08-22T12:30:00+03:00",
    signal_type: { id: -105, slug: "window_open", title: "Открыто окно", icon: "", color: "", category: "urgent" },
    allow_reply: false,
    reply_token: null,
  },
  {
    id: -106,
    entry_type: "signal",
    created_at: "2026-08-21T09:30:00+03:00",
    signal_type: { id: -106, slug: "thanks_for_help", title: "Помог на дороге", icon: "", color: "", category: "attention" },
    allow_reply: true,
    reply_token: "dev-figma-reply",
  },
  {
    id: -107,
    entry_type: "signal",
    created_at: "2026-08-20T23:11:00+03:00",
    signal_type: { id: -107, slug: "ran_red_light", title: "Проехал на красный", icon: "", color: "", category: "road" },
    allow_reply: false,
    reply_token: null,
  },
  {
    id: -108,
    entry_type: "signal",
    created_at: "2026-08-20T19:47:00+03:00",
    signal_type: { id: -108, slug: "urgent_contact_now", title: "Срочно свяжитесь", icon: "", color: "", category: "urgent" },
    allow_reply: true,
    reply_token: "dev-figma-reply",
  },
  {
    id: -109,
    entry_type: "signal",
    created_at: "2026-08-19T16:22:00+03:00",
    signal_type: { id: -109, slug: "nice_plate", title: "Красивый номер", icon: "", color: "", category: "attention" },
    allow_reply: false,
    reply_token: null,
  },
]

const GARAGE_VISUAL_MODES = new Set([
  "garage-android-inactive",
  "garage-android-active-off",
  "garage-android-active-on",
  "garage-ios-inactive",
  "garage-ios-active-off",
  "garage-ios-active-on",
  "license-garage-2digit",
  "license-garage-3digit",
])

function visualVehicle(twoDigit: boolean, active: boolean): VehicleCard {
  const plateNormalized = twoDigit ? "А172МУ77" : "А172МУ888"
  const plateDisplay = twoDigit ? "А 172 МУ 77" : "А 172 МУ 888"
  return {
    id: -1,
    plate_raw: plateNormalized,
    plate_normalized: plateNormalized,
    plate_display: plateDisplay,
    country_code: "RU",
    display_views: 987,
    real_search_count: 987,
    has_garage: active,
    garage_is_active: active,
    garage_owner_activity: active ? { color: "green", label: "онлайн" } : null,
    is_owner_viewing: active,
    current_status: {
      id: -1,
      slug: "special-series",
      title: "ОСОБАЯ СЕРИЯ",
      description: null,
      icon: "",
      color: "#6555D7",
    },
    signal_counters: [],
    card_share_count: 0,
  }
}

function visualGarage(active: boolean, privacyOn: boolean): Garage {
  return {
    id: active ? -1 : null,
    vehicle_id: -1,
    status: active ? "active" : "inactive",
    activated_at: active ? "2026-08-28T00:00:00Z" : null,
    expires_at: active ? "2027-08-28T00:00:00Z" : null,
    hide_attention_signals: active && privacyOn,
    clean_start_used: false,
    is_active: active,
    is_in_renewal_grace: false,
    renewal_grace_ends_at: active ? "2027-09-07T00:00:00Z" : null,
    can_renew: false,
    clean_start_is_free: active,
    clean_start_price_stars: active ? 0 : 9900,
    is_owner: active,
    can_activate: !active,
    activation_block_reason: null,
  }
}

export function GaragePage() {
  const { vehicleId } = useParams<{ vehicleId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { tg, hapticImpact, hapticNotification, showAlert, showConfirm } = useTelegram()
  const { locale, direction, t } = useI18n()
  const setStoredVehicle = useVehicleStore((state) => state.setCurrentVehicle)
  const authUser = useAuthStore((state) => state.user)
  const staffPreviewAccess = Boolean(authUser?.is_owner || authUser?.is_admin)
  const [garage, setGarage] = useState<Garage | null>(null)
  const [vehicle, setVehicle] = useState<VehicleCard | null>(null)
  const [journal, setJournal] = useState<JournalEntry[]>([])
  const [statusOptions, setStatusOptions] = useState<VehicleStatus[]>([])
  const [currentStatusId, setCurrentStatusId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<{ kind: "not-found" | "error"; message: string } | null>(null)
  const [loadRevision, setLoadRevision] = useState(0)
  const [isDeactivating, setIsDeactivating] = useState(false)
  const [isRenewing, setIsRenewing] = useState(false)
  const [isCleaning, setIsCleaning] = useState(false)
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [isUpdatingPrivacy, setIsUpdatingPrivacy] = useState(false)
  const statusMutationRef = useRef(false)
  const privacyMutationRef = useRef(false)
  const [openSheet, setOpenSheet] = useState<"activation" | "clean" | "deactivate" | "logbook" | "status" | "autoclan" | "partner" | null>(null)
  const [selectedStatusId, setSelectedStatusId] = useState<number | null | undefined>(undefined)
  const [sheetChecked, setSheetChecked] = useState(false)
  const [sheetNotice, setSheetNotice] = useState("")
  const [sheetNoticeKind, setSheetNoticeKind] = useState<"activation" | "clean" | "deactivate">("activation")

  const requestedVisualMode = import.meta.env.DEV ? new URLSearchParams(location.search).get("figma") : null
  const garageVisualMode = requestedVisualMode && GARAGE_VISUAL_MODES.has(requestedVisualMode) ? requestedVisualMode : null
  const isIOS = Boolean(
    garageVisualMode?.startsWith("garage-ios-") ||
      (!garageVisualMode && ["ios", "macos"].includes((tg?.platform || "").toLowerCase())),
  )
  const isActive = Boolean(garage?.is_active && garage.is_owner)
  const isRenewalGrace = Boolean(isActive && garage?.is_in_renewal_grace && garage.can_renew)
  const openDispatcherBot = () => {
    openSupportBot(tg, "dispatcher")
  }
  const query = new URLSearchParams(location.search)
  const requestedDevLogbook = import.meta.env.DEV ? query.get("garageLogbook")?.toLowerCase() : null
  const devLogbookState = requestedDevLogbook === "start" || requestedDevLogbook === "scrolled"
    ? requestedDevLogbook
    : null
  const activation = useGarageActivation("", normalizePromoInput(query.get("promo") || ""), query.get("affiliate") || "")

  const syncVehicleCard = async (targetVehicleId: number): Promise<VehicleCard | null> => {
    try {
      const card = await vehiclesApi.getCard(targetVehicleId)
      setVehicle(card)
      setStoredVehicle(card)
      return card
    } catch {
      return null
    }
  }

  useEffect(() => {
    const state = location.state as { garageNotice?: string; garageNoticeKind?: "activation" | "clean" | "deactivate" } | null
    if (!state?.garageNotice) return
    setSheetNotice(state.garageNotice)
    setSheetNoticeKind(state.garageNoticeKind || "activation")
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
    window.setTimeout(() => setSheetNotice(""), 3000)
  }, [location.pathname, location.search, location.state, navigate])

  useEffect(() => {
    if (garageVisualMode) {
      const active = !garageVisualMode.endsWith("inactive")
      const privacyOn = garageVisualMode.endsWith("active-on")
      const twoDigit = garageVisualMode.endsWith("2digit")
      setVehicle(visualVehicle(twoDigit, active))
      setGarage(visualGarage(active, privacyOn))
      setJournal([])
      setStatusOptions([])
      setCurrentStatusId(-1)
      setLoadError(null)
      setIsLoading(false)
      return
    }

    if (!vehicleId) {
      navigate("/", { replace: true })
      return
    }

    const id = Number(vehicleId)
    if (!Number.isInteger(id) || id <= 0) {
      navigate("/", { replace: true })
      return
    }

    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      setLoadError(null)
      setVehicle(null)
      setGarage(null)
      setJournal([])
      setStatusOptions([])
      setCurrentStatusId(null)

      try {
        const [card, garageState] = await Promise.all([
          vehiclesApi.getCard(id),
          garageApi.get(id),
        ])
        if (cancelled) return
        setVehicle(card)
        setStoredVehicle(card)
        setGarage(garageState)
        setCurrentStatusId(card.current_status?.id || null)
        if (garageState.is_active && garageState.is_owner && garageState.id !== null) {
          const [entries, statuses] = await Promise.all([
            garageApi.getJournal(garageState.id).catch(() => []),
            garageApi.getStatuses().catch(() => []),
          ])
          if (!cancelled) {
            setJournal(entries)
            setStatusOptions(statuses)
          }
        } else {
          setJournal([])
          setStatusOptions([])
        }
      } catch (error: any) {
        if (!cancelled) {
          const message = error instanceof ApiError
            ? error.detail
            : error?.message || t("partner.loadError")
          setLoadError({
            kind: error instanceof ApiError && error.status === 404 ? "not-found" : "error",
            message,
          })
          showAlert(message)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [garageVisualMode, vehicleId, showAlert, navigate, loadRevision, setStoredVehicle, t])

  useEffect(() => {
    if (!devLogbookState || isLoading || !isActive) return
    setJournal(DEV_LOGBOOK_FIGMA_FIXTURE)
    setOpenSheet("logbook")
  }, [devLogbookState, isActive, isLoading])

  const submitActivation = async () => {
    if (garageVisualMode) return
    hapticImpact("medium")
    const activated = await activation.activate()
    if (activated) {
      setGarage(activated)
      await syncVehicleCard(activated.vehicle_id)
      setOpenSheet(null)
      activation.reset()
      hapticNotification("success")
      setSheetNotice(t("garageEntry.activationSuccess"))
      setSheetNoticeKind("activation")
      window.setTimeout(() => setSheetNotice(""), 3000)
    } else {
      hapticNotification("error")
    }
  }

  const openActivationFlow = () => {
    if (garageVisualMode) return
    if (!garage?.can_activate) { showAlert(garage?.activation_block_reason || t("garageActivation.activateError")); return }
    activation.reset()
    activation.setPlate(vehicle?.plate_normalized || "")
    setOpenSheet("activation")
  }

  const loadJournal = async (): Promise<JournalEntry[] | null> => {
    if (garageVisualMode || garage?.id === null || garage?.id === undefined) return null
    try {
      const entries = await garageApi.getJournal(garage.id)
      setJournal(entries)
      return entries
    } catch (error: any) {
      showAlert(error?.message || t("garageEntry.loadJournalError"))
      return null
    }
  }

  const openLogbook = () => {
    if (!isActive || garage?.id == null) return
    const garageId = garage.id
    setOpenSheet("logbook")
    void (async () => {
      const entries = await loadJournal()
      if (!entries) return
      const latestSignal = entries.find((entry) => entry.entry_type === "signal")
      try {
        if (latestSignal) await garageApi.markSignalsRead(garageId, latestSignal.id)
        await garageApi.markUrgentRead(garageId)
      } catch (error: any) {
        showAlert(error?.message || t("common.error"))
      }
    })()
  }

  const openStatusSheet = () => {
    if (!isActive || garage?.id == null) return
    setSelectedStatusId(currentStatusId)
    setOpenSheet("status")
    void garageApi.getStatuses()
      .then(setStatusOptions)
      .catch((error: any) => showAlert(error?.message || t("garageEntry.loadStatusesError")))
  }

  const handleJournalReply = async (entry: JournalEntry) => {
    if (garage?.id == null || !entry.reply_token) return
    try {
      const { username } = await garageApi.resolveJournalReply(garage.id, entry.id, entry.reply_token)
      const url = `https://t.me/${username.replace(/^@/, "")}`
      if (tg?.openTelegramLink) tg.openTelegramLink(url)
      else window.open(url, "_blank", "noopener,noreferrer")
    } catch (error: any) { showAlert(error?.message || t("common.error")) }
  }

  const handlePrivacyToggle = async () => {
    if (
      garageVisualMode ||
      garage?.id === null ||
      garage?.id === undefined ||
      !isActive ||
      isUpdatingPrivacy ||
      privacyMutationRef.current
    ) return

    privacyMutationRef.current = true
    setIsUpdatingPrivacy(true)
    hapticImpact("light")
    try {
      const updatedGarage = await garageApi.updatePrivacy(garage.id, !garage.hide_attention_signals)
      setGarage(updatedGarage)
      await syncVehicleCard(updatedGarage.vehicle_id)
    } catch (error: any) {
      showAlert(error?.message || t("common.error"))
    } finally {
      privacyMutationRef.current = false
      setIsUpdatingPrivacy(false)
    }
  }

  const handleRenew = async () => {
    if (garageVisualMode || garage?.id === null || garage?.id === undefined || !isActive || isRenewing) return
    const renewalPrompt = `${garageUi(locale, "terminal.renew")} ${t("main.garage")} — 1 year?\n\n${t("garageSheet.cost")}: ${GARAGE_RENEWAL_PRICE_STARS} Stars.`
    if (!await showConfirm(renewalPrompt)) return
    setIsRenewing(true)
    hapticImpact("medium")
    try {
      const invoice = await garageApi.createRenewInvoice(garage.id)
      if (!await completeStarsInvoice(invoice)) return
      setGarage(await garageApi.get(garage.vehicle_id))
      hapticNotification("success")
    } catch (error: any) { showAlert(error?.message || t("common.error")) }
    finally { setIsRenewing(false) }
  }

  const handleSuggestStatus = () => {
    openSupportBot(tg, "status")
  }

  const handleStatusSelect = async (statusId: number | null) => {
    if (
      garageVisualMode ||
      garage?.id === null ||
      garage?.id === undefined ||
      !isActive ||
      isUpdatingStatus ||
      statusMutationRef.current
    ) return

    statusMutationRef.current = true
    setIsUpdatingStatus(true)
    hapticImpact("light")
    try {
      await garageApi.updateStatus(garage.id, statusId)
      setCurrentStatusId(statusId)
      setSelectedStatusId(statusId)
      await syncVehicleCard(garage.vehicle_id)
      setOpenSheet(null)
      hapticNotification("success")
    } catch (error: any) {
      showAlert(error?.message || t("common.error"))
    } finally {
      statusMutationRef.current = false
      setIsUpdatingStatus(false)
    }
  }

  const handleCleanPublicHistory = async () => {
    if (garageVisualMode || garage?.id === null || garage?.id === undefined || !isActive || isCleaning) return
    setIsCleaning(true)
    hapticImpact("medium")
    try {
      if (garage.clean_start_is_free) {
        await garageApi.cleanStart(garage.id)
      } else {
        const invoice = await garageApi.createCleanInvoice(garage.id)
        if (!await completeStarsInvoice(invoice)) return
      }
      const [journalResult, cardResult] = await Promise.allSettled([
        garageApi.getJournal(garage.id),
        vehiclesApi.getCard(garage.vehicle_id),
      ])
      if (journalResult.status === "fulfilled") setJournal(journalResult.value)
      if (cardResult.status === "fulfilled") {
        setVehicle(cardResult.value)
        setStoredVehicle(cardResult.value)
      }
      setOpenSheet(null)
      setSheetChecked(false)
      hapticNotification("success")
      setSheetNotice(locale === "ru" ? "История удалена" : t("garageLifecycle.cleanTitle"))
      setSheetNoticeKind("clean")
      window.setTimeout(() => setSheetNotice(""), 3000)
    } catch (error: any) { showAlert(error?.message || t("common.error")) }
    finally { setIsCleaning(false) }
  }

  const handleDeactivate = async () => {
    if (garageVisualMode || garage?.id === null || garage?.id === undefined || !isActive || isDeactivating) return
    setIsDeactivating(true)
    hapticImpact("medium")
    try {
      const deactivatedGarage = await garageApi.deactivate(garage.id)
      setGarage(deactivatedGarage)
      await syncVehicleCard(deactivatedGarage.vehicle_id)
      setJournal([])
      setStatusOptions([])
      setCurrentStatusId(null)
      setOpenSheet(null)
      setSheetChecked(false)
      setSheetNotice(t("garageLifecycle.deactivateTitle"))
      setSheetNoticeKind("deactivate")
      window.setTimeout(() => setSheetNotice(""), 3000)
      hapticNotification("success")
    } catch (error: any) { showAlert(error?.message || t("common.error")) }
    finally { setIsDeactivating(false) }
  }

  if (isLoading) return <div className="bbf-garage-loading" aria-label={t("common.loading")} />

  if (loadError || !vehicle) {
    return (
      <div
        className="bbf-garage-loading"
        role="alert"
        aria-label={loadError?.message || t("partner.loadError")}
        data-state={loadError?.kind || "error"}
      >
        <button type="button" className="bbf-garage-header-button back" onClick={() => navigate(-1)} dir={direction}>
          {t("common.back")}
        </button>
        <button type="button" className="bbf-garage-terminal start" onClick={() => setLoadRevision((value) => value + 1)} dir={direction}>
          {garageUi(locale, "feature.retry")}
        </button>
      </div>
    )
  }

  const openPreviewJournal = () => {
    if (staffPreviewAccess && !isActive) {
      setJournal(DEV_LOGBOOK_FIGMA_FIXTURE)
      setOpenSheet("logbook")
      return
    }
    openLogbook()
  }

  const openPreviewStatus = () => {
    if (staffPreviewAccess && !isActive) {
      setSelectedStatusId(currentStatusId)
      setOpenSheet("status")
      void garageApi.getStatuses()
        .then(setStatusOptions)
        .catch((error: any) => showAlert(error?.message || t("garageEntry.loadStatusesError")))
      return
    }
    openStatusSheet()
  }

  const togglePreviewPrivacy = () => {
    if (staffPreviewAccess && !isActive) {
      hapticImpact("light")
      setGarage((current) => current ? { ...current, hide_attention_signals: !current.hide_attention_signals } : current)
      return
    }
    void handlePrivacyToggle()
  }

  const actionHandlers = { journal: openPreviewJournal, status: openPreviewStatus, autoclan: () => setOpenSheet("autoclan"), privacy: togglePreviewPrivacy, "clean-start": () => { setSheetChecked(false); setOpenSheet("clean") }, autotrade: () => navigate("/commercial/autotrade"), insurance: () => navigate("/commercial/insurance"), pitstop: () => navigate("/commercial/pitstop"), autohelp: () => navigate("/commercial/techhelp"), companion: () => { hapticImpact("light"); setOpenSheet("partner") }, "bb-help": openDispatcherBot }

  const statusPreview = openSheet === "status" && selectedStatusId !== undefined
    ? selectedStatusId === null
      ? null
      : statusOptions.find((status) => status.id === selectedStatusId) || vehicle.current_status
    : vehicle.current_status
  const renderedVehicle = statusPreview === vehicle.current_status
    ? vehicle
    : { ...vehicle, current_status: statusPreview }

  return (
    <GarageBackdrop
      vehicle={renderedVehicle}
      isIOS={isIOS}
      active={isActive}
      privacyOn={Boolean(garage?.hide_attention_signals)}
      activationNote={garage?.can_activate === false ? garage.activation_block_reason || t("garageActivation.activateError") : t("garageEntry.activationNote")}
      actionHandlers={garageVisualMode ? {} : actionHandlers}
      onBack={garageVisualMode ? undefined : () => navigate(-1)}
      stopDisabled={!isActive || isDeactivating}
      startDisabled={(isActive && !isRenewalGrace) || (!isActive && !garage?.can_activate) || activation.busy || isRenewing}
      startLabel={isRenewalGrace ? garageUi(locale, "terminal.renew") : garageUi(locale, "terminal.start")}
      onStop={garageVisualMode ? undefined : () => { setSheetChecked(false); setOpenSheet("deactivate") }}
      onStart={garageVisualMode ? undefined : isRenewalGrace ? () => void handleRenew() : openActivationFlow}
    >
      {openSheet === "activation" && (
        <GarageActivationSheet
          isIOS={isIOS}
          plate={activation.plate}
          plateError={activation.plateError}
          promoCode={activation.promoCode}
          promoValid={activation.promoValid}
          promoError={activation.promoError}
          bonusYears={activation.bonusYears}
          checked={activation.checked}
          busy={activation.busy}
          onPlateChange={activation.setPlate}
          onPromoChange={activation.setPromoCode}
          onToggle={() => activation.setChecked(!activation.checked)}
          onBack={() => { setOpenSheet(null); activation.reset() }}
          onConfirm={() => void submitActivation()}
        />
      )}
      {openSheet === "clean" && (
        <GarageLifecycleSheet
          kind="clean"
          isIOS={isIOS}
          checked={sheetChecked}
          busy={isCleaning}
          free={Boolean(garage?.clean_start_is_free)}
          onToggle={() => setSheetChecked((value) => !value)}
          onBack={() => { setOpenSheet(null); setSheetChecked(false) }}
          onConfirm={() => {
            if (staffPreviewAccess && !isActive) {
              setOpenSheet(null)
              setSheetChecked(false)
              return
            }
            void handleCleanPublicHistory()
          }}
        />
      )}
      {openSheet === "deactivate" && (
        <GarageLifecycleSheet
          kind="deactivate"
          isIOS={isIOS}
          checked={sheetChecked}
          busy={isDeactivating}
          onToggle={() => setSheetChecked((value) => !value)}
          onBack={() => { setOpenSheet(null); setSheetChecked(false) }}
          onConfirm={() => void handleDeactivate()}
        />
      )}
      {openSheet === "logbook" && (
        <GarageLogbookSheet
          isIOS={isIOS}
          entries={journal}
          devScrollState={devLogbookState}
          onBack={() => setOpenSheet(null)}
          onReply={devLogbookState || (staffPreviewAccess && !isActive) ? () => undefined : (entry) => void handleJournalReply(entry)}
        />
      )}
      {openSheet === "status" && (
        <GarageStatusSheet
          isIOS={isIOS}
          statuses={statusOptions}
          currentStatusId={currentStatusId}
          selectedStatusId={selectedStatusId}
          busy={isUpdatingStatus}
          onSelect={setSelectedStatusId}
          onSuggestOwn={handleSuggestStatus}
          onBack={() => { setSelectedStatusId(undefined); setOpenSheet(null) }}
          onApply={() => {
            if (selectedStatusId === undefined) return
            if (staffPreviewAccess && !isActive) {
              setCurrentStatusId(selectedStatusId)
              setSelectedStatusId(undefined)
              setOpenSheet(null)
              return
            }
            void handleStatusSelect(selectedStatusId)
          }}
        />
      )}
      {openSheet === "autoclan" && (
        <AutoclanPage embedded onClose={() => setOpenSheet(null)} />
      )}
      {openSheet === "partner" && (
        <PartnerPage embedded onClose={() => setOpenSheet(null)} />
      )}
      {sheetNotice && <GarageSuccessNotification isIOS={isIOS} text={sheetNotice} kind={sheetNoticeKind} />}
    </GarageBackdrop>
  )
}
